// packages import
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Typography
} from '@mui/material'
import { FC, useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../../common/Alert'
import { CustomModal } from '../../common/CustomModal'
import { UpdatePassword } from './UpdatePassword'
import { UpdateName } from './UpdateName'
import { UpdateEmail } from './UpdateEmail'
import { UpdateInitials } from './UpdateInitials'
// others
import {
  Maybe,
  UpdateRoleInput,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useUnlockUserMutation,
  User,
  UserRole,
  UserStatus,
  useUpdateRoleMutation
} from '../../../../generated'
import {
  CANCEL,
  LOCKED,
  SOMETHING_WENT_WRONG,
  UNLOCK,
  USER_UNLOCK_SUCCESS,
  USER_UPDATE_SUCCESS
} from '../../../constants'
import { AppContext } from '../../../context/AppContext'
import { AuthContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { DispatchAndState, ErrorException } from '../../../interfaceTypes'
import { StatusBox } from '../../../theme/styleComponents'
import { formatDateTime } from '../../../utils'
import { anchorOrigin, transformOrigin } from '../../../theme/styleConstants'
import { UserAction, UserActionType, UserState } from '../userReducer'
import { CustomController } from '../../common/CustomController'
import { yupResolver } from '@hookform/resolvers/yup'
import { updateRoleSchema } from '../../../validationSchema'
import SelectDelegates from '../SelectDelegates'
import UpdateUserGeneralInfo from './UpdateDepartments'
import { UpdateMfaSettings } from './UpdateMfaSettings'

export const EditUser: FC<DispatchAndState<UserState, UserAction>> = ({
  state,
  dispatch: userDispatch,
  refetch
}): JSX.Element => {
  const { dispatch, isEdit, rolesData } = useContext(AppContext)
  const { isAdmin, isSuperAdmin } = useContext(AuthContext)
  const [switchStatus, setSwitchStatus] = useState<boolean>(false)
  const { currentUser, usersData } = state || {}
  const { id, status, roles, hourlyRate, paralegals, lockedAt } = currentUser || {}
  const userRolesDefault = roles?.map(role => role?.role)
  const methods = useForm<UpdateRoleInput>({
    mode: 'all',
    resolver: yupResolver(updateRoleSchema),
    defaultValues: {
      id,
      roles: userRolesDefault,
      hourlyRate: usersData?.find(user => user?.id === id)?.hourlyRate,
      delegateIds: paralegals?.map(paralegal => paralegal?.id)
    }
  })

  const { handleSubmit, control, setValue, watch } = methods

  const [updateRoleMutation, { loading: rolesLoader }] = useUpdateRoleMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          }
        )
      }
    },

    onCompleted(data) {
      if (data) {
        const {
          updateRole: { response, user }
        } = data
        if (response) {
          const { status } = response

          if (status === 200) {
            refetch?.()
            setValue('roles', userRolesDefault as UserRole[])
            Alert.success(USER_UPDATE_SUCCESS)
          }
          if (usersData) {
            const updatedUsers = usersData.map(toUpdateUser => {
              return toUpdateUser?.id === id ? (user as Maybe<User>) : toUpdateUser
            })

            userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: updatedUsers })
            userDispatch({ type: UserActionType.SET_CURRENT_USER, currentUser: user as Maybe<User> })
          }
        }
      }
    }
  })

  const handleClose = () => isEdit && dispatch({ type: ActionType.SET_IS_EDIT, isEdit: !isEdit })

  const [activateUserMutation, { loading: activateLoader }] = useActivateUserMutation({
    onError() {
      Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      if (data) {
        const {
          activateUser: { response, user }
        } = data
        if (response) {
          const { status } = response

          if (status === 200) {
            Alert.success(USER_UPDATE_SUCCESS)
          }

          if (usersData) {
            const updatedUsers = usersData.map(toUpdateUser => {
              return toUpdateUser?.id === id ? (user as Maybe<User>) : toUpdateUser
            })

            userDispatch({ type: UserActionType.SET_CURRENT_USER, currentUser: user as Maybe<User> })
            userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: updatedUsers })
          }
        }
      }
    }
  })

  const [deactivateUserMutation, { loading: deactivateLoader }] = useDeactivateUserMutation({
    onError() {
      Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      if (data) {
        const {
          deactivateUser: { response, user }
        } = data
        if (response) {
          const { status } = response

          if (status === 200) {
            Alert.success(USER_UPDATE_SUCCESS)
          }

          if (usersData) {
            const updatedUsers = usersData.map(toUpdateUser => {
              return toUpdateUser?.id === id ? (user as Maybe<User>) : toUpdateUser
            })

            userDispatch({ type: UserActionType.SET_CURRENT_USER, currentUser: user as Maybe<User> })
            userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: updatedUsers })
          }
        }
      }
    }
  })

  const [unlockUserMutation, { loading: unlockLoader }] = useUnlockUserMutation({
    onError() {
      Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      const { response, user } = data?.unlockUser || {}
      if (response?.status !== 200) return

      Alert.success(USER_UNLOCK_SUCCESS)

      // The mutation only returns the lock fields, so merge them into the rows we hold.
      const unlocked = { ...currentUser, lockedAt: user?.lockedAt ?? null } as Maybe<User>
      userDispatch({ type: UserActionType.SET_CURRENT_USER, currentUser: unlocked })

      if (usersData) {
        const updatedUsers = usersData.map(toUpdateUser => (toUpdateUser?.id === id ? unlocked : toUpdateUser))
        userDispatch({ type: UserActionType.SET_USERS_DATA, usersData: updatedUsers })
      }
    }
  })

  const unlockHandler = async () => {
    await unlockUserMutation({ variables: { user: { userId: id || '' } } })
  }

  const updateStatusHandler = async () => {
    if (switchStatus) {
      await activateUserMutation({
        variables: {
          user: {
            userId: id || ''
          }
        }
      })
    } else {
      await deactivateUserMutation({
        variables: {
          user: {
            userId: id || ''
          }
        }
      })
    }
  }

  const handleChangeSwitch = (event: SelectChangeEvent) => setSwitchStatus(!switchStatus)

  const onSubmit: SubmitHandler<UpdateRoleInput> = async (data: UpdateRoleInput) => {
    await updateRoleMutation({
      variables: {
        updateRoleInput: {
          ...data,
          id: id || ''
        }
      }
    })

    setValue('delegateIds', data.delegateIds)
  }

  useEffect(() => {
    setSwitchStatus(status === UserStatus.Active)
    // eslint-disable-next-line
  }, [isEdit])

  useEffect(() => {
    if (userRolesDefault) {
      setValue('roles', userRolesDefault as UserRole[])
    }
    // eslint-disable-next-line
  }, [isEdit, id])

  useEffect(() => {
    if (paralegals) {
      setValue('hourlyRate', hourlyRate)
      setValue(
        'delegateIds',
        paralegals?.map(paralegal => paralegal?.id || '')
      )
    }
    // eslint-disable-next-line
  }, [isEdit, id])

  const switchBorderColor = switchStatus ? 'green' : 'red'
  const loading = activateLoader || deactivateLoader || rolesLoader || unlockLoader
  const statusUpdateDisabled =
    (switchStatus && status === UserStatus.Active) || (!switchStatus && status === UserStatus.Deactivated)

  return (
    <CustomModal title="Edit User" isOpen={isEdit} handleClose={handleClose}>
      {/* Name fields - First Name and Last Name on same row */}
      <UpdateName state={state} dispatch={userDispatch} refetch={refetch} />

      {/* Initials - half width field */}
      <UpdateInitials state={state} dispatch={userDispatch} refetch={refetch} />

      {/* Email field */}
      <UpdateEmail state={state} dispatch={userDispatch} refetch={refetch} />

      {/* Status - Active/Deactivate */}
      <Box pb={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="h5" fontSize="14px">
              Status:
            </Typography>
          </Box>

          <IconButton onClick={updateStatusHandler} color="primary" aria-label="save" disabled={statusUpdateDisabled}>
            <SaveRoundedIcon color={!statusUpdateDisabled ? 'primary' : 'disabled'} />
          </IconButton>
        </Box>

        <Switch name="status" onChange={handleChangeSwitch} checked={switchStatus} sx={{ ml: '-12px' }} />

        <StatusBox variant="subtitle2" borderColor={switchBorderColor}>
          {switchStatus ? 'Active' : 'Deactivate'}
        </StatusBox>
      </Box>

      {/* Locked by too many failed sign-ins. The lock never expires; this is the only way to lift it. */}
      {lockedAt && (isAdmin || isSuperAdmin) && (
        <Box pb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box flex={1}>
              <Typography variant="h5" fontSize="14px">
                Sign-in:
              </Typography>

              <Typography variant="body2" mt={1}>
                Locked on {formatDateTime(lockedAt)} after too many failed sign-in attempts.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              color="primary"
              onClick={unlockHandler}
              disabled={unlockLoader}
              endIcon={unlockLoader && <CircularProgress size={20} />}
            >
              {UNLOCK}
            </Button>
          </Box>

          <StatusBox variant="subtitle2" borderColor="red" mt={1}>
            {LOCKED}
          </StatusBox>
        </Box>
      )}

      {/* Divider */}
      <Divider sx={{ my: 3 }} />

      {/* Roles and other fields */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box pb={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box flex={1}>
                <Typography variant="h5" fontSize="14px">
                  Roles:
                </Typography>
              </Box>

              <IconButton type="submit" aria-label="save" disabled={status !== UserStatus.Active}>
                <SaveRoundedIcon color={status === UserStatus.Active ? 'primary' : 'disabled'} />
              </IconButton>
            </Box>

            <FormControl variant="outlined" sx={{ marginTop: 1.5, marginBottom: 1 }} fullWidth>
              <InputLabel id="role-select">Roles</InputLabel>

              <Controller
                name="roles"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Select
                      {...field}
                      labelId="role-select"
                      fullWidth
                      label="Roles"
                      multiple
                      value={field.value || []}
                      MenuProps={{ anchorOrigin, transformOrigin }}
                      onChange={event => field.onChange(event.target.value)}
                    >
                      {rolesData?.map((item, index) => {
                        const { label, value } = item

                        return (
                          <MenuItem value={value} key={`${label}-${index}`}>
                            {label}
                          </MenuItem>
                        )
                      })}
                    </Select>

                    <CustomController
                      {...field}
                      controllerName="hourlyRate"
                      controllerLabel="Hourly Rate"
                      fieldType="number"
                      dollarAdornment
                    />
                  </Box>
                )}
              />
            </FormControl>

            {(watch('roles')?.includes(UserRole.Attorney) || watch('roles')?.includes(UserRole.Investigator)) && (
              <SelectDelegates userState={state} />
            )}
          </Box>
        </form>
      </FormProvider>

      <UpdateUserGeneralInfo state={state as UserState} refetch={refetch} />
      <UpdatePassword state={state} dispatch={userDispatch} />

      {/* Only a super admin may reach another account's second factor, and the API says so too. */}
      {isSuperAdmin && (
        <>
          <Divider sx={{ my: 3 }} />
          <UpdateMfaSettings state={state} dispatch={userDispatch} refetch={refetch} />
        </>
      )}

      <DialogActions sx={{ padding: '30px 0 0' }}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleClose}
          disabled={loading}
          aria-label={CANCEL}
          endIcon={loading && <CircularProgress size={24} />}
        >
          {CANCEL}
        </Button>
      </DialogActions>
    </CustomModal>
  )
}
